import AttractionRedirectItem from "@/components/molecules/attractions/AttractionRedirectItem";
import DateTimeSelector from "@/components/molecules/attractions/DateTimeSelector";
import { IVenueBasket } from "@app-contexts/dashboard/features/box-office/basket/interfaces/IVenueBasket";
import { AttractionItem } from "@app-contexts/dashboard/features/events/data/types/events";
import { IPublicBasket } from "@app-contexts/public/features/basket/interfaces/IPublicBasket";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { enums } from "@shared/constants";
import DonationsContainer from "@shared/modules/events/components/donations/DonationsContainer/DonationsContainer.web";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import React, { ReactElement } from "react";
import { AttractionTicketTypes } from "@shared/modules/events/components/attractions/AttractionTicketTypes";
import { stringToSlug } from "@app/core/src/shared/utils/text/utils";
import Box from "@app/core/src/shared/components/ui-kit/layout/Box/Box.web";
import AttractionCardSlider from "./AttractionCardSlider/AttractionCardSlider";

// Use the AttractionItem type directly since it now properly handles optional/required fields
type AttractionItemType = AttractionItem;

export interface AttractionItemsTabProps {
  basket: IPublicBasket | IVenueBasket;
  isBasketUpdating: boolean;
  event: PublicBaseEventSerializer;
  attractionItems: AttractionItemType[];
  scrollMargin?: number;
}

const AttractionItemsTab: React.FC<AttractionItemsTabProps> = ({
  basket,
  event,
  attractionItems,
  isBasketUpdating,
  scrollMargin,
}) => {
  const sortedItems = attractionItems.sort(
    (a, b) => a.sort_order - b.sort_order
  );

  function getItemDisplay(item: AttractionItemType): ReactElement | void {
    // display the event selector (opens the calendar)
    if (
      item.ids &&
      item.ids.length > 0 &&
      (item.type === enums.ATTRACTION_CHILD_TYPE_MAP.EVENT.id ||
        item.type === enums.ATTRACTION_CHILD_TYPE_MAP.SINGLE_EVENT.id) &&
      event?.venue?.id
    ) {
      return (
        <Box id={stringToSlug(item.title)} scrollMarginTop={scrollMargin}>
          <DateTimeSelector
            key={item.title}
            event={event}
            basket={basket}
            sectionTitle={item.title}
            sectionDescription={item.description}
            buttonText={
              (item.button_text ||
                event?.initiate_purchase_button ||
                "ipbd_buy_tickets") as keyof typeof enums.INITIATE_PURCHASE_BUTTON_DESCRIPTION_MAP_BY_ID
            }
            eventIdArray={item.ids ?? []}
            specialEventsArray={item.attraction_special_events ?? []}
            itemIdArray={item.item_ids ?? []}
            // false positive error
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            venueSlug={event.venue.slug!}
            promptForQuantity={Boolean(item.prompt_for_quantity)}
            ticketTypeSelectionRequired={Boolean(
              item.ticket_type_selection_required
            )}
            itemType={item.type}
            isSoldOut={item.is_sold_out}
          />
        </Box>
      );
    }

    if (item.type === enums.ATTRACTION_CHILD_TYPE_MAP.TICKET_TYPES.id) {
      return (
        <Box id={stringToSlug(item.title)} scrollMarginTop={scrollMargin}>
          <AttractionTicketTypes
            attractionItem={item}
            attractionEvent={event}
          />
        </Box>
      );
    }

    if (item.type === enums.ATTRACTION_CHILD_TYPE_MAP.REDIRECT.id) {
      return (
        <Box id={stringToSlug(item.title)} scrollMarginTop={scrollMargin}>
          <AttractionRedirectItem
            buttonText={
              (item.button_text ||
                event?.initiate_purchase_button ||
                "ipbd_buy_tickets") as keyof typeof enums.INITIATE_PURCHASE_BUTTON_DESCRIPTION_MAP_BY_ID
            }
            sectionTitle={item.title}
            sectionDescription={item.description}
            sectionUrl={item.url || ""}
          />
        </Box>
      );
    }

    // display the item slider for products/memberships
    if (
      (item.type === enums.ATTRACTION_CHILD_TYPE_MAP.PRODUCT.id ||
        item.type === enums.ATTRACTION_CHILD_TYPE_MAP.MEMBERSHIP.id) &&
      item.ids &&
      item.ids.length > 0 &&
      event?.venue
    ) {
      return (
        <Box id={stringToSlug(item.title)} scrollMarginTop={scrollMargin}>
          <AttractionCardSlider
            key={item.title}
            venue={event.venue}
            attractionItem={item}
            isBasketUpdating={isBasketUpdating}
          />
        </Box>
      );
    }

    return undefined;
  }

  return (
    <Flex direction="column" gap="md" w="full">
      <>
        {sortedItems.map((item) => getItemDisplay(item))}

        {event?.charities_of_choice?.[0] && (
          <Box id="donations" scrollMarginTop={scrollMargin}>
            <DonationsContainer
              event={event}
              isBasketUpdating={isBasketUpdating}
              causeId={event.charities_of_choice?.[0]}
            />
          </Box>
        )}
      </>
    </Flex>
  );
};

export default AttractionItemsTab;
